<script setup>
import { computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import {
  useStore,
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import AgentMessageThread from '../components/AgentMessageThread.vue';
import AgentMessageInput from '../components/AgentMessageInput.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const route = useRoute();
const store = useStore();

const conversationId = computed(() => Number(route.params.conversationId));

const getConversation = useFunctionGetter(
  'agentConversations/getConversationById',
  conversationId
);
const currentUser = useMapGetter('getCurrentUser');
const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const { uiSettings, updateUISettings } = useUISettings();

const showCopilotButton = computed(() =>
  isFeatureEnabledonAccount.value(currentAccountId.value, FEATURE_FLAGS.CAPTAIN)
);
const isCopilotOpen = computed(() => uiSettings.value.is_copilot_panel_open);
const toggleCopilot = () =>
  updateUISettings({
    is_copilot_panel_open: !isCopilotOpen.value,
    is_contact_sidebar_open: false,
  });

const otherAgent = computed(() => {
  const conv = getConversation.value;
  if (!conv) return null;
  const uid = currentUser.value?.id;
  return conv.initiator.id === uid ? conv.participant : conv.initiator;
});

const loadMessages = async () => {
  if (!conversationId.value) return;
  await store.dispatch('agentMessages/fetchMessages', {
    conversationId: conversationId.value,
  });
  if (getConversation.value?.unread_count > 0) {
    store.dispatch('agentMessages/markRead', conversationId.value);
  }
};

onMounted(loadMessages);
watch(conversationId, loadMessages);

const handleSend = async ({ content, signedIds = [] }) => {
  if (!content.trim() && !signedIds.length) return;
  await store.dispatch('agentMessages/sendMessage', {
    conversationId: conversationId.value,
    content,
    signedIds,
  });
};
</script>

<template>
  <div class="flex flex-col h-full w-full min-w-0">
    <div
      v-if="otherAgent"
      class="flex items-center gap-3 px-4 h-[3.25rem] shrink-0 border-b border-n-weak"
    >
      <Avatar
        :src="otherAgent.avatar_url"
        :name="otherAgent.name"
        :size="32"
        :status="otherAgent.availability_status"
      />
      <span class="font-medium text-sm text-n-slate-12 truncate">
        {{ otherAgent.name }}
      </span>
      <div class="ml-auto flex items-center">
        <Button
          v-if="showCopilotButton"
          v-tooltip.bottom="$t('CONVERSATION.SIDEBAR.COPILOT')"
          ghost
          slate
          sm
          class="!rounded-full transition-all duration-[250ms] ease-out"
          :class="{ 'bg-n-alpha-2 !text-n-iris-9': isCopilotOpen }"
          icon="i-woot-captain"
          @click="toggleCopilot"
        />
      </div>
    </div>
    <AgentMessageThread
      :conversation-id="conversationId"
      class="flex-shrink flex-grow basis-px"
    />
    <div class="flex relative flex-col bg-n-surface-1">
      <AgentMessageInput :conversation-id="conversationId" @send="handleSend" />
    </div>
  </div>
</template>
